import { useCallback } from "react";
import type { ChangeEvent, MouseEvent } from "react";
import { useHistory } from "react-router-dom";
import type { FiltersDropDownItem } from "../../../../components/FiltersDropDown/FiltersDropDown";
import type { ListPageState } from "./useListPageState";
import { createChangePerPageHandler } from "../../../../components/Pagination/util";

export interface ListPageHandlers {
  handleSearch: (e: ChangeEvent<HTMLInputElement>) => void;
  handleClearSearch: () => void;
  changePage: (offset: number) => void;
  changePerPage: (perPage: number) => void;
  applyStatus: (items: FiltersDropDownItem[]) => void;
  applyAccountManager: (items: FiltersDropDownItem[]) => void;
  applyCustomerServiceRep: (items: FiltersDropDownItem[]) => void;
  handleRowClick: (e: MouseEvent, itemId: string, basePath: string) => void;
  closeDownloadReportSlideout: () => void;
}

/**
 * Custom hook that provides common handlers for list pages
 */
export const useListPageHandlers = (
  state: ListPageState,
  setTableData: (data: any[]) => void
): ListPageHandlers => {
  const history = useHistory();

  const handleSearch = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      state.setOffset(0);
      state.setSearchQuery(e.target.value);
    },
    [state]
  );

  const handleClearSearch = useCallback(() => {
    state.setSearchQuery("");
    state.setQuery({ q: undefined });
    state.setOffset(0);
  }, [state]);

  const changePage = useCallback(
    (offset: number) => {
      state.setOffset(offset);
      setTableData([]);
    },
    [state, setTableData]
  );

  const changePerPage = createChangePerPageHandler(
    state.offset,
    state.setPerPage,
    state.setOffset
  );

  const applyStatus = useCallback(
    (items: FiltersDropDownItem[]) => {
      state.setSelectedStatus(items);
      state.setQuery({ status: items.map((item) => item.value) });
      changePage(0);
    },
    [state, changePage]
  );

  const applyAccountManager = useCallback(
    (items: FiltersDropDownItem[]) => {
      state.setSelectedAccountManagers(items);
      state.setQuery({ account_managers: items.map((item) => item.value) });
      changePage(0);
    },
    [state, changePage]
  );

  const applyCustomerServiceRep = useCallback(
    (items: FiltersDropDownItem[]) => {
      state.setSelectedCustomerServiceReps(items);
      state.setQuery({
        customer_service_reps: items.map((item) => item.value),
      });
      changePage(0);
    },
    [state, changePage]
  );

  const handleRowClick = useCallback(
    (_e: MouseEvent, itemId: string, basePath: string) => {
      const params = new URLSearchParams();
      if (state.query.q) {
        params.append("q", state.query.q);
      }
      params.append("offset", String(state.offset));
      params.append("perPage", String(state.perPage));
      (
        (state.query?.status ?? []).filter((status) => !!status) as string[]
      ).forEach((status) => params.append("status", status));
      (
        (state.query?.account_managers ?? []).filter(
          (account_manager) => !!account_manager
        ) as string[]
      ).forEach((account_manager) =>
        params.append("account_managers", account_manager)
      );
      (
        (state.query?.customer_service_reps ?? []).filter(
          (customer_service_rep) => !!customer_service_rep
        ) as string[]
      ).forEach((customer_service_rep) =>
        params.append("customer_service_reps", customer_service_rep)
      );
      history.push(`${basePath}/${itemId}?${params}`);
    },
    [state, history]
  );

  const closeDownloadReportSlideout = useCallback(() => {
    state.setDownloadReportSlideOut(false);
  }, [state]);

  return {
    handleSearch,
    handleClearSearch,
    changePage,
    changePerPage,
    applyStatus,
    applyAccountManager,
    applyCustomerServiceRep,
    handleRowClick,
    closeDownloadReportSlideout,
  };
};
